import React, { useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  InputBase,
  IconButton,
  Tooltip,
  Button,
  CircularProgress,
  Collapse
} from '@mui/material';
import {
  Search,
  FilterList,
  PlayArrow,
  CalendarToday,
  ChevronRight,
  Close,
  Chat,
  Refresh,
  VolumeUp,
  ViewList,
  KeyboardArrowUp
} from '@mui/icons-material';
import { Meeting } from '../../types';
import MeetingDetails from './MeetingDetails';
import { useAuth } from '../../context/AuthContext';

interface MeetingsSidebarProps {
  meetings: Meeting[];
  selectedMeeting: Meeting | null;
  onSelectMeeting: (meeting: Meeting) => void;
  onCollapse?: () => void;
  onClose?: () => void;
  onOpenInChat?: () => void;
  onRefreshMeetings?: () => void;
  isLoadingMeetings?: boolean;
  isMobile?: boolean;
  onParticipantAdded?: (meetingId: string, email: string) => void;
}

const MeetingsSidebar: React.FC<MeetingsSidebarProps> = ({
  meetings,
  selectedMeeting,
  onSelectMeeting,
  onCollapse,
  onClose,
  onOpenInChat,
  onRefreshMeetings,
  isLoadingMeetings,
  isMobile,
  onParticipantAdded
}) => {
  const { user } = useAuth();
  const [tab, setTab] = useState(0); // 0: Today, 1: Past, 2: My Meetings
  const [searchQuery, setSearchQuery] = useState('');
  const [isMeetingListCollapsed, setIsMeetingListCollapsed] = useState(false);
  const [autoPlayMeetingId, setAutoPlayMeetingId] = useState<string | null>(null);

  const isMeetingToday = (m: Meeting): boolean => {
    if (m.date.toLowerCase() === 'today') return true;

    if (m.meeting_time) {
      const d = new Date(m.meeting_time);
      if (!isNaN(d.getTime())) {
        const today = new Date();
        if (
          d.getFullYear() === today.getFullYear() &&
          d.getMonth() === today.getMonth() &&
          d.getDate() === today.getDate()
        ) {
          return true;
        }
      }
    }

    const todayFormatted = new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
    if (m.date === todayFormatted) return true;

    // In current project context Oct 16, 2026 is Today
    if (m.date.includes('Oct 16, 2026')) return true;

    return false;
  };

  const isMeetingPast = (m: Meeting): boolean => {
    return !isMeetingToday(m);
  };

  const isMyMeeting = (m: Meeting): boolean => {
    const userEmail = user?.email?.toLowerCase();
    if (!userEmail) return true;
    if (m.host_email?.toLowerCase() === userEmail) return true;
    return (m.participants || []).some((p) => p.email?.toLowerCase() === userEmail);
  };

  const filteredMeetings = meetings.filter((m) => {
    // 1. Tab filter
    if (tab === 0) {
      if (!isMeetingToday(m)) return false;
    } else if (tab === 1) {
      if (!isMeetingPast(m)) return false;
    } else if (tab === 2) {
      if (!isMyMeeting(m)) return false;
    }

    // 2. Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchTitle = m.title.toLowerCase().includes(q);
      const matchDate = m.date.toLowerCase().includes(q);
      const matchHost = m.host_email?.toLowerCase().includes(q) || false;
      const matchPart = (m.participants || []).some(
        (p) => p.name.toLowerCase().includes(q) || (p.email && p.email.toLowerCase().includes(q))
      );
      if (!matchTitle && !matchDate && !matchHost && !matchPart) {
        return false;
      }
    }

    return true;
  });

  const getEmptyMessage = () => {
    if (tab === 0) return 'No meetings scheduled or recorded for today.';
    if (tab === 1) return 'No past meetings found.';
    return 'No meetings found for your account.';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 1.2, width: '100%', minWidth: 0 }}>
      {/* Top Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.05rem', color: 'var(--hp-text)' }}>
            Meetings
          </Typography>
          <Typography variant="caption" sx={{ color: 'var(--hp-text-secondary)', bgcolor: 'var(--hp-surface-raised)', px: 0.8, py: 0.2, borderRadius: '8px', border: '1px solid var(--hp-border)', fontWeight: 600 }}>
            {filteredMeetings.length} / {meetings.length}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          {onRefreshMeetings && (
            <Tooltip title="Sync with Cloud Run API">
              <IconButton
                size="small"
                onClick={onRefreshMeetings}
                disabled={isLoadingMeetings}
                sx={{
                  color: 'var(--hp-text-secondary)',
                  bgcolor: 'var(--hp-surface-raised)',
                  p: 0.5,
                  '&:hover': { color: 'var(--hp-primary)' }
                }}
              >
                <Refresh
                  fontSize="small"
                  sx={{
                    animation: isLoadingMeetings ? 'spin 1s linear infinite' : 'none',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }}
                />
              </IconButton>
            </Tooltip>
          )}

          {/* Close / Collapse button if supported */}
          {onClose ? (
            <Tooltip title="Close drawer">
              <IconButton size="small" onClick={onClose} sx={{ color: 'var(--hp-text-secondary)' }}>
                <Close fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : onCollapse ? (
            <Tooltip title="Collapse meetings sidebar">
              <IconButton size="small" onClick={onCollapse} aria-label="Collapse meetings sidebar" sx={{ color: 'var(--hp-text-secondary)' }}>
                <ChevronRight fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      </Box>

      {/* List / Focus View Toggle Bar */}
      {selectedMeeting && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'var(--hp-surface)',
            px: 1.2,
            py: 0.6,
            borderRadius: '10px',
            border: '1px solid var(--hp-border)',
            flexShrink: 0
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minWidth: 0, flex: 1, pr: 1 }}>
            <CalendarToday sx={{ fontSize: 16, color: 'var(--hp-primary)', flexShrink: 0 }} />
            <Typography variant="caption" sx={{ color: 'var(--hp-text)', fontWeight: 700, fontSize: '0.78rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isMeetingListCollapsed ? `Focus: ${selectedMeeting.title}` : `All Meetings (${meetings.length})`}
            </Typography>
          </Box>

          <Button
            size="small"
            startIcon={isMeetingListCollapsed ? <ViewList sx={{ fontSize: 15 }} /> : <KeyboardArrowUp sx={{ fontSize: 16 }} />}
            onClick={() => setIsMeetingListCollapsed(!isMeetingListCollapsed)}
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
              color: 'var(--hp-primary)',
              fontWeight: 700,
              py: 0.2,
              px: 1,
              borderRadius: '8px',
              bgcolor: 'var(--hp-primary-light)',
              flexShrink: 0,
              '&:hover': { bgcolor: '#d4e1ff' }
            }}
          >
            {isMeetingListCollapsed ? `Show List (${meetings.length})` : 'Hide List ▲'}
          </Button>
        </Box>
      )}

      {/* Collapsible Meeting List Section */}
      <Collapse in={!isMeetingListCollapsed || !selectedMeeting} sx={{ flexShrink: 0 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Tabs
              value={tab}
              onChange={(e, v) => setTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                minHeight: '30px',
                '& .MuiTab-root': {
                  minHeight: '30px',
                  textTransform: 'none',
                  fontSize: '0.8rem',
                  fontWeight: 500,
                  color: 'var(--hp-text-secondary)',
                  py: 0,
                  px: 1.2,
                  minWidth: 'auto'
                },
                '& .Mui-selected': {
                  color: 'var(--hp-primary)',
                  bgcolor: 'var(--hp-primary-light)',
                  borderRadius: '10px',
                  fontWeight: 700
                },
                '& .MuiTabs-indicator': { display: 'none' }
              }}
            >
              <Tab label="Today" />
              <Tab label="Past" />
              <Tab label="My Meetings" />
            </Tabs>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box className="neumorphic-input" sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, py: 0.4 }}>
              <Search sx={{ color: 'var(--hp-text-secondary)', fontSize: 17 }} />
              <InputBase
                placeholder="Search meetings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ flex: 1, fontSize: '0.82rem', color: 'var(--hp-text)' }}
              />
              {searchQuery && (
                <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ p: 0.2, color: 'var(--hp-text-secondary)' }}>
                  <Close sx={{ fontSize: 15 }} />
                </IconButton>
              )}
            </Box>
            <IconButton size="small" sx={{ bgcolor: 'var(--hp-surface-raised)', boxShadow: '2px 2px 5px var(--hp-shadow-dark), -2px -2px 5px var(--hp-shadow-light)', borderRadius: '10px' }}>
              <FilterList fontSize="small" />
            </IconButton>
          </Box>

          {/* Meeting List items */}
          <Box sx={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.8, pr: 0.5 }}>
            {isLoadingMeetings ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2, gap: 1 }}>
                <CircularProgress size={20} sx={{ color: 'var(--hp-primary)' }} />
                <Typography variant="caption" color="var(--hp-text-secondary)">Syncing meetings from Cloud Run...</Typography>
              </Box>
            ) : filteredMeetings.length === 0 ? (
              <Box sx={{ p: 1.5, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.8, bgcolor: 'var(--hp-surface)', borderRadius: '10px' }}>
                <CalendarToday sx={{ color: 'var(--hp-text-secondary)', fontSize: 24, opacity: 0.6 }} />
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'var(--hp-text)', fontSize: '0.82rem' }}>
                  {getEmptyMessage()}
                </Typography>
                <Typography variant="caption" color="var(--hp-text-secondary)" sx={{ fontSize: '0.74rem' }}>
                  Sync with Cloud Run or invite notetaker.
                </Typography>
                {onRefreshMeetings && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={onRefreshMeetings}
                    startIcon={<Refresh sx={{ fontSize: 12 }} />}
                    sx={{ textTransform: 'none', borderRadius: '6px', fontSize: '0.72rem', mt: 0.4 }}
                  >
                    Sync Meetings
                  </Button>
                )}
              </Box>
            ) : (
              filteredMeetings.map((meeting) => {
                const isSelected = selectedMeeting ? meeting.id === selectedMeeting.id : false;
                return (
                  <Box
                    key={meeting.id}
                    onClick={() => {
                      onSelectMeeting(meeting);
                      setAutoPlayMeetingId(null);
                      if (isMobile && onOpenInChat) {
                        onOpenInChat();
                      }
                    }}
                    className="neumorphic-card"
                    sx={{
                      p: 1.2,
                      cursor: 'pointer',
                      bgcolor: isSelected ? 'var(--hp-surface-raised)' : 'var(--hp-surface)',
                      border: isSelected ? '1px solid rgba(79, 107, 255, 0.3)' : '1px solid transparent',
                      boxShadow: isSelected ? '2px 2px 6px var(--hp-shadow-dark)' : 'none',
                      transition: 'all 0.15s ease',
                      '&:hover': { bgcolor: 'var(--hp-surface-raised)' }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 1, minWidth: 0, flex: 1 }}>
                        <Box sx={{ mt: 0.2, color: 'var(--hp-primary)', flexShrink: 0 }}>
                          <CalendarToday sx={{ fontSize: 16 }} />
                        </Box>
                        <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
                          <Typography sx={{ fontWeight: isSelected ? 700 : 600, fontSize: '0.86rem', color: 'var(--hp-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {meeting.title}
                          </Typography>
                          <Typography variant="caption" color="var(--hp-text-secondary)" sx={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.72rem' }}>
                            {meeting.date} • {meeting.time}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexShrink: 0, ml: 1 }}>
                        {meeting.audioUrl && (
                          <Tooltip title="Audio recording available">
                            <VolumeUp sx={{ fontSize: 15, color: 'var(--hp-success)' }} />
                          </Tooltip>
                        )}
                        {isMobile && onOpenInChat && isSelected ? (
                          <Button
                            size="small"
                            startIcon={<Chat sx={{ fontSize: 12 }} />}
                            sx={{ textTransform: 'none', fontSize: '0.7rem', py: 0.2, px: 0.6, minWidth: 'auto', bgcolor: 'var(--hp-primary)', color: 'white', borderRadius: '6px' }}
                          >
                            Chat
                          </Button>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onSelectMeeting(meeting);
                              setIsMeetingListCollapsed(true);
                              setAutoPlayMeetingId(meeting.id);
                            }}
                            sx={{ color: 'var(--hp-primary)', bgcolor: 'var(--hp-primary-light)', p: 0.4 }}
                          >
                            <PlayArrow sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Collapse>

      {/* Selected Meeting Details Card (Moves up to take full space for transcript) */}
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <MeetingDetails
          meeting={selectedMeeting}
          onPlay={() => setIsMeetingListCollapsed(true)}
          onTabChange={() => setIsMeetingListCollapsed(true)}
          autoPlay={Boolean(autoPlayMeetingId && selectedMeeting && autoPlayMeetingId === selectedMeeting.id)}
          onParticipantAdded={onParticipantAdded}
        />
      </Box>
    </Box>
  );
};

export default MeetingsSidebar;
